/**
 * Environment Variable Schema
 *
 * This is the SINGLE SOURCE OF TRUTH for all environment variables.
 * Add new variables here - everything else is automatic.
 */

/**
 * Define all environment variables here.
 * - Key: the export name (without VITE_ prefix)
 * - Value: description of what it's for
 *
 * The VITE_ prefix is automatically added for validation.
 * All variables are REQUIRED - build will fail if missing.
 */
export const ENV_VARS = {
  // Server URLs
  HELLO_SERVER_URL: 'Backend API URL for HelloHeart services',
  HELLO_SERVER_TOKEN: 'Authentication token for HelloHeart API',

  // Branch.io Deep Links
  BRANCH_URL: 'Branch.io deep link URL for app downloads',
  BRANCH_OTP_URL: 'Branch.io deep link URL with OTP support',

  // Google reCAPTCHA
  RECAPTCHA_SITE_KEY: 'Google reCAPTCHA Enterprise site key',

  // Mixpanel Analytics
  MIXPANEL_TOKEN: 'Mixpanel analytics token',
  MIXPANEL_API_HOST: 'Mixpanel API host',
  MIXPANEL_DEBUG: 'Enable Mixpanel debug mode',

  // Google Tag Manager
  GTM_ID: 'Google Tag Manager container ID',

  // External Events (Encrypted Analytics)
  EVENTS_EXTERNAL_PUBLIC_KEY: 'RSA public key for encrypting external analytics events (PEM format)',
  EVENTS_EXTERNAL_API_URL: 'External events API endpoint hostname (without https://)',
  EVENTS_EXTERNAL_API_TOKEN: 'Bearer token for authenticating with the external events API',
  EVENTS_EXTERNAL_ENCRYPTION_ENABLED: 'Feature flag to enable/disable client-side encryption of external events (true/false)',

  // Support Contact Info
  SUPPORT_EMAIL: 'Support email address',
  SUPPORT_NUMBER: 'Support phone number',
  FORETHOUGHT_API_KEY: 'Forethought chatbot API key',

  // App Metadata
  APP_VERSION: 'Application version string',

  // Promo Code
  ACCESS_PILOT_PROMO_CODE: 'ACCESS Pilot promo code for enrollment validation',

  // CMS Integration Service — injected by Jenkinsfile-Build per environment
  CMS_SERVICE_URL: 'Base URL for the CMS Integration Service (eligibility/alignment)',
} as const;

/**
 * Optional env vars: typed + available via env.ts, but NOT validated/required at
 * build time (the validator in vite.config.ts only enforces ENV_VARS).
 */
export const OPTIONAL_ENV_VARS = {
  // RND-35692: render the phone/OTP account-creation path instead of the
  // password form. Optional (not build-validated) → unset resolves to OFF, so
  // the password path stays the default.
  PASSWORDLESS_OTP_ENABLED: 'Feature flag (true/false) — enable phone/OTP account creation instead of password. Default off.',
  // RND-33872: internal-testing pilot mock. When 'true' (and never in a
  // production build, regardless of this flag), every CMS Integration Service
  // and hello-server call short-circuits to a canned success response so
  // friends-and-family testers can run the full happy path on staging with no
  // CMS dependency. Default off.
  PILOT_MOCK_ENABLED: 'Feature flag (true/false) — mock CMS/hello-server calls for the internal-testing pilot happy path. Never active in production. Default off.',
  // Google Places Autocomplete + Address Validation for the shipping address
  // step. Optional (not build-validated) → unset falls back to a plain manual
  // address form (no autocomplete/validation), so local dev/tests without the
  // key still work. Restrict this key (HTTP referrer + API restrictions) in
  // Google Cloud Console before using it in a real environment.
  GOOGLE_PLACES_API_KEY: 'Google Places/Address Validation API key for the shipping address step. Unset = plain manual address form.',
} as const;
